import React, {useCallback, useState, useMemo, useRef, useEffect} from "react";
import {Form, Upload, Input, Button, Tooltip} from "antd";
import {CameraOutlined, LoadingOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import classnames from "classnames";
import {compensationOptionsToText, issueOptionsToText, statusOptionsToText} from "./utils.jsx";
import {UploadClient} from "../../http/UploadClient";
import {EventBus} from "../../bus/EventBus";
import {getInputComponent} from "../../components/InputComponent";
import "./issue-modal.css";
import {SquareButton} from "../../components/SquareButton.jsx";

const imageProps = {
    width: 500,
    height: 500
};

export const IssueForm = ({
    form,
    onSave,
    onCancel,
    declineReasons = null,
    declineReasonLabel = "בחרו את סוג התקלה",
    isLoading: externalLoading = false
}) => {
    const uploadPreviewUrlRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const resetUploadPreview = useCallback(() => {
        if (uploadPreviewUrlRef.current) {
            URL.revokeObjectURL(uploadPreviewUrlRef.current);
            uploadPreviewUrlRef.current = null;
        }
        setImagePreview(null);
        setUploadedImage(null);
    }, []);

    const handleImageUpload = useCallback(
        async file => {
            if (!file) {
                return false;
            }

            try {
                setIsUploadingImage(true);
                const objectUrl = URL.createObjectURL(file);
                uploadPreviewUrlRef.current = objectUrl;
                setImagePreview(objectUrl);

                const formData = new FormData();
                formData.append("file", file);
                const {imageUrl, imageId} = await UploadClient.upload(
                    "PUT",
                    `/api/image?width=${imageProps.width}&height=${imageProps.height}&crop=fill&gravity=center&colors=1`,
                    formData
                );

                setUploadedImage({imageUrl, imageId});
                setIsUploadingImage(false);
            } catch (e) {
                setIsUploadingImage(false);
                resetUploadPreview();
                EventBus.triggerError(
                    "server-error",
                    {content: {description: "לא הצלחנו להעלות את התמונה"}},
                    e.message
                );
            }

            return false;
        },
        [resetUploadPreview]
    );

    const handleClearImage = useCallback(() => {
        resetUploadPreview();
    }, [resetUploadPreview]);

    const onFinish = useCallback(
        async formData => {
            setIsLoading(true);
            const dataToSave = {
                ...formData,
                compensationType: Array.isArray(formData.compensationType)
                    ? formData.compensationType.join(", ")
                    : formData.compensationType,
                ...(uploadedImage ? {imageUrl: uploadedImage.imageUrl, imageId: uploadedImage.imageId} : {})
            };
            await onSave(dataToSave);
            setIsLoading(false);
            handleClearImage();
            form?.resetFields();
        },
        [onSave, uploadedImage, handleClearImage, form]
    );

    const declineOrderReasons = useMemo(() => {
        if (declineReasons) {
            return Object.entries(declineReasons).map(([key, value]) => ({name: key, label: value}));
        }
        return Object.entries(issueOptionsToText).map(([key, value]) => ({name: key, label: value}));
    }, [declineReasons]);

    const compensationOptions = useMemo(() => {
        return Object.entries(compensationOptionsToText).map(([key, value]) => ({name: key, label: value}));
    }, []);

    const statusOptions = useMemo(() => {
        return Object.entries(statusOptionsToText).map(([key, value]) => ({name: key, label: value}));
    }, []);

    useEffect(() => {
        return () => {
            if (uploadPreviewUrlRef.current) {
                URL.revokeObjectURL(uploadPreviewUrlRef.current);
                uploadPreviewUrlRef.current = null;
            }
        };
    }, []);

    const isFormLoading = isLoading || isUploadingImage || externalLoading;

    const fields = useMemo(
        () => [
            {
                name: "issueReason",
                label: declineReasonLabel,
                type: "select",
                options: declineOrderReasons,
                placeholder: "בחרו מהאפשרויות"
            },
            {
                name: "issueComment",
                className: "issueComment",
                type: "textarea",
                rows: 3,
                placeholder: "הוספת תיאור התקלה, חשוב לסכום במשפט את השורה התחתונה "
            },
            {
                label: "הוספת תמונה",
                name: "imageUrl",
                type: "custom",
                optional: true,
                customComponent: (
                    <div className="issue-modal-image-upload-container">
                        <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={handleImageUpload}
                            disabled={isUploadingImage}
                            className="issue-modal-image-upload">
                            {isUploadingImage ? (
                                <span className="issue-modal-image-upload-placeholder">
                                    <LoadingOutlined
                                        style={{fontSize: 20, marginLeft: 10}}
                                        onPointerEnterCapture={() => {}}
                                        onPointerLeaveCapture={() => {}}
                                    />
                                    מעלה תמונה...
                                </span>
                            ) : imagePreview ? (
                                <img src={imagePreview} alt="issue upload" />
                            ) : (
                                <span className="issue-modal-image-upload-placeholder">
                                    <CameraOutlined
                                        style={{fontSize: 20, marginLeft: 10}}
                                        onPointerEnterCapture={() => {}}
                                        onPointerLeaveCapture={() => {}}
                                    />
                                    הוספת תמונה
                                </span>
                            )}
                        </Upload>
                        {imagePreview && !isUploadingImage && (
                            <button type="button" className="issue-modal-image-clear" onClick={handleClearImage}>
                                נקה תמונה
                            </button>
                        )}
                    </div>
                )
            },
            {
                label: "הונפק פיצוי ללקוח",
                type: "custom",
                optional: true,
                info: "קוד קופון+ סכום= פיצוי להזמנה עתידית\nסכום בלבד= זיכוי שבוצע בשדה קוד קופון במעמד ההזמנה ",
                customComponent: (
                    <div className="issue-modal-compensation-container">
                        <div className="issue-modal-compensation-field">
                            <Form.Item name="compensationOffered" noStyle>
                                <Input placeholder="שם קוד קופון" className="issue-modal-compensation-name" />
                            </Form.Item>
                        </div>
                        <div className="issue-modal-compensation-field">
                            <Form.Item name="compensationAmount" noStyle>
                                <Input placeholder="סכום" className="issue-modal-compensation-amount" />
                            </Form.Item>
                        </div>
                    </div>
                )
            },
            {
                name: "compensationType",
                label: "פורמט הפיצוי ללקוח",
                type: "select",
                optional: true,
                multiple: true,
                options: compensationOptions,
                placeholder: "בחרו מהאפשרויות"
            },
            {
                name: "compensationComment",
                type: "textarea",
                optional: true,
                rows: 2,
                placeholder: "תיאור איך טופלה התקלה במידה ויש "
            },
            {
                name: "status",
                label: "סטטוס טיפול",
                type: "select",
                options: statusOptions,
                placeholder: "בחרו מהאפשרויות"
            },
            {
                name: "shouldShowToProvider",
                checkboxText: "להציג לספק בהעלאת החשבונית",
                type: "checkbox",
                optional: true
            }
        ],
        [
            declineOrderReasons,
            compensationOptions,
            statusOptions,
            declineReasonLabel,
            isUploadingImage,
            imagePreview,
            handleImageUpload,
            handleClearImage
        ]
    );

    return (
        <Form form={form} onFinish={onFinish} layout="vertical">
            {fields.map(input => (
                <div key={input.name || input.label} className={classnames("form-modal-input-line", input.className)}>
                    {input.label ? (
                        <label>
                            <span className="form-modal-input-label">{input.label}</span>
                            {input.info ? (
                                <Tooltip title={input.info}>
                                    <QuestionCircleOutlined
                                        disabled={input.disabled}
                                        className="form-modal-input-info"
                                    />
                                </Tooltip>
                            ) : null}
                        </label>
                    ) : null}
                    {getInputComponent(input)}
                </div>
            ))}

            <Form.Item>
                <div style={{display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px"}}>
                    <SquareButton className="form-modal-save-button" loading={isFormLoading} htmlType="submit">
                        שמירה
                    </SquareButton>
                </div>
            </Form.Item>
        </Form>
    );
};
